import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { ArrowRight, Clock } from "lucide-react";

const POSTS = [
  {
    slug: "alaska-brown-bear-spring-vs-fall",
    title: "Alaska Brown Bear: Spring vs. Fall",
    excerpt: "Two seasons, two completely different hunts. Here's what to expect from each — and which one is right for you.",
    category: "Destination Report",
    readTime: "8 min",
    image: "https://dispatchhunts.com/wp-content/uploads/2024/12/Alaskan-fall-brown-bear-hunt.jpg",
  },
  {
    slug: "what-to-expect-first-african-safari",
    title: "What to Expect on Your First African Safari",
    excerpt: "South Africa, Namibia, or Tanzania? Rifle or bow? Seven days or fourteen? A straight-talk guide for first-time Africa hunters.",
    category: "Planning Guide",
    readTime: "10 min",
    image: "https://dispatchhunts.com/wp-content/uploads/2025/03/2b3bd60b-3821-40e2-b178-75f16c50775e.jpeg",
  },
  {
    slug: "marco-polo-sheep-tajikistan",
    title: "Marco Polo Sheep: The Ultimate Mountain Hunt",
    excerpt: "What Tajikistan demands physically, logistically, and mentally. Why it's worth it. What most people get wrong going in.",
    category: "Destination Report",
    readTime: "12 min",
    image: "https://dispatchhunts.com/wp-content/uploads/2024/12/Tajikistan-Marco-Polo.jpg",
  },
  {
    slug: "choosing-elk-outfitter-montana",
    title: "Choosing the Right Elk Outfitter in Montana",
    excerpt: "Not all elk outfitters are created equal. Here's what to look for, what to avoid, and the questions most hunters forget to ask.",
    category: "Species Guide",
    readTime: "7 min",
    image: "https://dispatchhunts.com/wp-content/uploads/2024/12/montana-elk-rifle-hunt.jpg",
  },
  {
    slug: "mountain-hunt-pack-list",
    title: "The Complete Pack List for a Mountain Hunt",
    excerpt: "Gear that matters, gear that doesn't, and the stuff most guys forget. Built from 15 years of high-altitude experience.",
    category: "Gear & Prep",
    readTime: "6 min",
    image: "https://dispatchhunts.com/wp-content/uploads/2025/10/IMG_2826.jpeg",
  },
  {
    slug: "kamchatka-brown-bear-worth-it",
    title: "Kamchatka Brown Bear: Worth the Trip?",
    excerpt: "Russia's Kamchatka Peninsula is one of the last great brown bear destinations. Here's the honest breakdown: logistics, costs, and what you're really getting into.",
    category: "Destination Report",
    readTime: "9 min",
    image: "https://dispatchhunts.com/wp-content/uploads/2024/12/Brown-Bear-Kamchatka.jpg",
  },
];

const CATEGORIES = ["All", "Destination Report", "Planning Guide", "Species Guide", "Gear & Prep"];

export default function Journal() {
  return (
    <>
      <Helmet>
        <title>Field Reports | Dispatch Hunts</title>
        <meta name="description" content="Destination reports, species guides, and straight-talk planning advice from 15+ years in the field." />
      </Helmet>

      {/* Hero */}
      <section className="section-dark grain pt-28 pb-12" data-testid="journal-hero">
        <div className="container-dh text-center max-w-3xl mx-auto">
          <p className="text-label text-brass mb-4">Field Reports</p>
          <h1 className="font-display text-4xl sm:text-5xl font-bold text-bone mb-4" data-testid="journal-headline">
            Notes from the field.
          </h1>
          <p className="font-body text-bone/50 max-w-xl mx-auto">
            Destination reports, species breakdowns, and planning advice from someone who's actually been there. No filler. No content marketing. Just useful information.
          </p>
        </div>
      </section>

      {/* Posts */}
      <section className="section-chalk py-12 lg:py-20" data-testid="journal-posts">
        <div className="container-dh">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {POSTS.map((post) => (
              <article key={post.slug} className="group bg-bone overflow-hidden shadow-sm hover:shadow-lg transition-shadow" data-testid={`journal-post-${post.slug}`}>
                <div className="relative h-52 overflow-hidden">
                  <img src={post.image} alt={post.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" loading="lazy" />
                  <div className="absolute top-3 left-3">
                    <span className="text-label text-[10px] bg-carbon/80 text-bone px-3 py-1 backdrop-blur-sm">{post.category}</span>
                  </div>
                </div>
                <div className="p-6">
                  <h3 className="font-display text-lg font-bold text-carbon mb-2 group-hover:text-brass transition-colors">{post.title}</h3>
                  <p className="font-body text-sm text-smoke leading-relaxed mb-4">{post.excerpt}</p>
                  <div className="flex items-center justify-between pt-4 border-t border-carbon/5">
                    <span className="flex items-center gap-1 text-xs text-smoke font-body"><Clock className="w-3 h-3" /> {post.readTime}</span>
                    <span className="text-label text-brass text-[10px] group-hover:translate-x-1 transition-transform flex items-center gap-1">
                      Read <ArrowRight className="w-3 h-3" />
                    </span>
                  </div>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="section-dark grain py-16" data-testid="journal-cta">
        <div className="container-dh text-center max-w-2xl mx-auto">
          <p className="font-body text-bone/50 mb-6">Have a question about a specific destination or species? Skip the blog and reach out directly.</p>
          <Link to="/apply" className="btn-brass" data-testid="journal-apply-btn">Apply for a Consultation <ArrowRight className="w-4 h-4" /></Link>
        </div>
      </section>
    </>
  );
}
